import React, { useEffect, useState } from 'react';
import { Grid, Link, List, ListItem, Skeleton, Stack, Typography } from '@mui/material';
import { FailureGradeMatrix } from '@Components/deteriorationModel/gradeSetupTab/FailureGradeMatrix';
import { DeteriorationModelFailureGradeRange, FailureGradeCell } from '@Types/failureGrade.types';
import { FailureGradePieChart } from '@Components/deteriorationModel/gradeSetupTab/FailureGradePieChart';
import { buildPieChartData } from '@Components/deteriorationModel/gradeSetupTab/utils.GradeMatrix';
import { XrangePointOptionsObject } from 'highcharts';
import {
	selectDeteriorationModel,
	selectFailureGradeMatrixIsLoading,
	selectFailureGradeMatrixIsSuccessful,
	selectFailureGradeMatrixResults,
} from '@Selectors';
import { useDispatch } from 'react-redux';
import { getFailureGradeMatrixAction } from '@Actions';
import { useGlobalization } from '@Translations';
import { WEAVE_FONT } from '@Utils/fonts';

interface GradeSetupHeaderProps {
	onEditRanges: () => void;
}

export const GradeSetupHeader = ({ onEditRanges }: GradeSetupHeaderProps): JSX.Element => {
	const { t } = useGlobalization();
	const bodyText = { fontFamily: WEAVE_FONT, fontSize: '0.8125rem', color: '#6b7280' } as const;
	return (
		<Stack sx={{ gap: 1 }}>
			<Typography
				sx={{ fontFamily: WEAVE_FONT, fontSize: '1.25rem', fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>
				{t('Grade Setup')}
			</Typography>
			<Typography sx={bodyText}>
				{t(
					'Assets are graded by combining two factors: how likely they are to fail now (Failure Probability) and how soon they will reach critical condition (Remaining Useful Life).',
				)}
			</Typography>
			<List dense disablePadding sx={{ pl: 2, listStyleType: 'disc' }}>
				<ListItem sx={{ display: 'list-item', pl: 0, pb: 0 }}>
					<Typography sx={bodyText}>
						{t(
							// eslint-disable-next-line prettier/prettier
							'Failure Probability (FP) - the likelihood of failure at the asset\'s current age',
						)}
					</Typography>
				</ListItem>
				<ListItem sx={{ display: 'list-item', pl: 0, pt: 0 }}>
					<Typography sx={bodyText}>
						{t(
							'Remaining Useful Life (RUL) - years until the asset reaches the Failure Threshold (80% Failure Probability)',
						)}
					</Typography>
				</ListItem>
			</List>
			<Typography sx={bodyText}>
				{t(
					// eslint-disable-next-line prettier/prettier
					'An asset with high FP and low RUL needs the most urgent attention - it\'s already deteriorated and will cross the threshold soon. The matrix below shows how your assets are distributed across these two dimensions.',
				)}
			</Typography>
			<Typography sx={bodyText}>
				<Link
					component="button"
					onClick={onEditRanges}
					sx={{
						fontFamily: WEAVE_FONT,
						fontSize: '0.8125rem',
						fontWeight: 700,
						textDecoration: 'none',
						cursor: 'pointer',
						verticalAlign: 'baseline',
					}}>
					{t('Edit Ranges')}
				</Link>{' '}
				{t('to adjust the boundaries to match your planning horizons.')}
			</Typography>
		</Stack>
	);
};

export const DeteriorationModelGradeSetup = () => {
	const { t } = useGlobalization();
	const [failureGradeMatrix, setFailureGradeMatrix] = useState<FailureGradeCell[][]>([]);

	const [pieChartData, setPieChartData] = useState<XrangePointOptionsObject[]>([]);

	const matrixResult = selectFailureGradeMatrixResults();

	const deteriorationModel = selectDeteriorationModel();
	const currentDeteriorationModel = deteriorationModel.deteriorationModel;
	const dispatch = useDispatch();
	const isLoading = selectFailureGradeMatrixIsLoading();
	const isSuccessful = selectFailureGradeMatrixIsSuccessful();

	const [ranges, setRanges] = useState<DeteriorationModelFailureGradeRange>({
		rulRanges: [3, 6, 12, 30, 1000],
		fpRanges: [100, 90, 70, 50, 30],
	});

	const [showEditRangesDialog, setShowEditRangesDialog] = useState<boolean>(false);

	useEffect(() => {
		// Keep the seeded default ranges until the backend matrix actually resolves;
		// matrixResult is a defined-but-empty object before then, so overwriting here
		// would flash empty axes on first load.
		if (!matrixResult?.matrix?.length) return;

		setRanges({
			rulRanges: [...(matrixResult.ruls ?? [])],
			fpRanges: [...(matrixResult.failure_probabilities ?? [])],
		});

		const transformedData = matrixResult.matrix.map((row, rIdx) =>
			row.map((cell, cIdx) => ({ ...cell, x: rIdx, y: cIdx })),
		);
		setFailureGradeMatrix(transformedData);

		// PieChart Data
		setPieChartData(buildPieChartData(transformedData, t));
	}, [matrixResult, t]);

	useEffect(() => {
		// Don't dispatch if no model ID, if already loading, or if previous request failed
		if (currentDeteriorationModel?.id === undefined || isLoading || isSuccessful === false) return;

		// Check if we already have data for this specific model - avoid unnecessary API calls
		const hasDataForCurrentModel =
			matrixResult &&
			matrixResult.matrix.length > 0 &&
			matrixResult.failure_probabilities.length > 0 &&
			matrixResult.ruls.length > 0;

		// Check if cached data belongs to the current run results
		const isSameRunResults = matrixResult?.latestRunResultId === currentDeteriorationModel?.latestRunResultsId;

		if (hasDataForCurrentModel && isSameRunResults) {
			return;
		}
		dispatch(
			getFailureGradeMatrixAction({
				modelId: currentDeteriorationModel?.id || '',
				latestRunResultId: currentDeteriorationModel?.latestRunResultsId || '',
			}),
		);
	}, [
		dispatch,
		currentDeteriorationModel?.id,
		currentDeteriorationModel?.latestRunResultsId,
		matrixResult?.latestRunResultId,
		isLoading,
		isSuccessful,
	]);

	// Show skeleton loading when initially loading data (no cached data exists)
	if (isLoading && !matrixResult?.matrix?.length) {
		return (
			<Grid container spacing={2}>
				<Grid item xs={6}>
					<Skeleton variant="rectangular" width="100%" height={400} />
				</Grid>
				<Grid item xs={6}>
					<Skeleton variant="circular" width={300} height={300} sx={{ margin: 'auto' }} />
				</Grid>
			</Grid>
		);
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<GradeSetupHeader onEditRanges={() => setShowEditRangesDialog(true)} />
			</Grid>
			<Grid item xs={6}>
				<FailureGradeMatrix
					failureGradeMatrix={failureGradeMatrix}
					ranges={ranges}
					showEditRangesDialog={showEditRangesDialog}
					setShowEditRangesDialog={setShowEditRangesDialog}
				/>
			</Grid>
			<Grid item xs={6}>
				<FailureGradePieChart data={pieChartData} />
			</Grid>
		</Grid>
	);
};
